import { useState } from 'react';
import { Card, CardHeader, CardBody, Collapse, Button, Row, Col, Spinner } from 'reactstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import AssignInspectorModal from './AssignInspectorModal';
import TransactionDetailsModal from './TransactionDetailsModal';
import UpdatesRequiredModal from './UpdatesRequiredModal';
import { formatDateTime, formatFileSize, formatPrice, formatStringWithUnderscores } from 'lib/utils/formatters';
import profile_placeholder from "assets/images/profile_placeholder.png";
import { updatePrimaryInspectionReportStatus } from 'endpoints/builder/builderPropertyListingDetails';

interface CompanyInspectionProps {
    companyInspectionData: any; // Define the type based on your data structure
    inspectionId?: string;
    setInspectionId?: (id: string) => void;
}

const CompanyInspection = ({ companyInspectionData, inspectionId, setInspectionId }: CompanyInspectionProps) => {
    const [isOpen, setIsOpen] = useState(true); // Changed from false to true
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [updatesModalOpen, setUpdatesModalOpen] = useState(false);

    const queryClient = useQueryClient();

    // Function to get badge color based on status
    const getStatusBadgeColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'bg-warning';
            case 'assigned':
            case 'approved':
            case 'accepted':
            case 'scheduled':
                return 'bg-success';
            case 'rejected':
            case 'call_to_action':
                return 'bg-danger';
            case 'completed':
                return 'bg-info';
            default:
                return 'bg-secondary';
        }
    };

    // Add mutation for call to action following PropertyDocuments pattern
    const updateReportMutation = useMutation({
        mutationFn: (data: {
            inspectionReportId: string;
            rejectionReason?: string;
            status: string;
        }) => updatePrimaryInspectionReportStatus(data),
        onSuccess: (response) => {
            if (response?.status_code === 200) {
                toast.success(response?.response || 'Inspection report status updated successfully');
                // Invalidate queries to refresh data
                queryClient.invalidateQueries({ queryKey: ['propertyUnitInspectionDetails'] });
            } else {
                toast.error('Failed to send call to action');
            }
        },
        onError: (error: any) => {
            console.error('Error updating inspection report status:', error);
            toast.error(error?.response?.data?.message || 'Failed to send call to action');
        }
    });

    const toggle = () => setIsOpen(!isOpen);
    const toggleAssignModal = () => setIsAssignModalOpen(!isAssignModalOpen);

    // Add download handler function
    const handleDownloadReport = () => {
        if (companyInspectionData?.PrimaryInspectionReport?.reportFileUrl) {
            window.open(companyInspectionData.PrimaryInspectionReport.reportFileUrl, '_blank');
        }
    };

    // Handle call to action confirmation following PropertyDocuments pattern
    const handleCallToActionConfirm = async (finalReason: string) => {
        if (!companyInspectionData?.PrimaryInspectionReport?.id || !finalReason.trim()) {
            toast.error('Rejection reason is required');
            return;
        }

        // Close modal first
        setUpdatesModalOpen(false);

        // Process the call to action with reason
        try {
            await updateReportMutation.mutateAsync({
                inspectionReportId: companyInspectionData.PrimaryInspectionReport.id,
                rejectionReason: finalReason,
                status: 'call_to_action'
            });
        } catch (error) {
            // Error is handled in mutation onError
        }
    };

    // Handle modal cancellation following PropertyDocuments pattern  
    const handleCallToActionCancel = () => {
        // Simply close modal without any status change
        setUpdatesModalOpen(false);
        // No API call, no status change - everything remains as it was
    };

    // Sanitize phone for tel: links (retain leading +, remove other non-digits)
    const sanitizePhoneForTel = (raw?: string) => {
        if (!raw) return '';
        const trimmed = String(raw).trim();
        const hasPlus = trimmed.startsWith('+');
        const digitsOnly = trimmed.replace(/[^\d]/g, '');
        return hasPlus ? `+${digitsOnly}` : digitsOnly;
    };

    return (
        <Card className="mb-1">
            <CardHeader onClick={toggle} style={{ cursor: 'pointer' }} className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Company Inspection</h6>
                <div className="d-flex align-items-center">
                    <span className={`badge ${getStatusBadgeColor(companyInspectionData?.status)} text-light me-2`}>{formatStringWithUnderscores(companyInspectionData?.status) || 'N/A'}</span>
                    <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} style={{ fontSize: '1.25rem' }}></i>
                </div>
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    <Row className="mb-2">
                        <Col xs={4}>Status</Col>
                        <Col xs={8} className="text-end">
                            <span className={`badge ${getStatusBadgeColor(companyInspectionData?.status)} text-light`}>{formatStringWithUnderscores(companyInspectionData?.status) || 'N/A'}</span>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col xs={4}>Allocation Date</Col>
                        <Col xs={8} className="text-end">{formatDateTime(companyInspectionData?.createdAt) || 'N/A'}</Col>
                    </Row>
                    <Row className="mb-2">
                        <Col xs={4}>Schedule Vist Date</Col>
                        <Col xs={8} className="text-end">{formatDateTime(companyInspectionData?.scheduledVisits?.scheduled_date) || 'N/A'}</Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={4}>Payment</Col>
                        <Col xs={8} className="text-end">
                            {`$${formatPrice(companyInspectionData.inspection_amount)}`}
                        </Col>
                    </Row>
                    {/* Inspector Box */}
                    <div className="mb-3">
                        <div className="mb-3">
                            <h6 className="mb-2">Inspector</h6>
                            <div className="border mb-0 d-flex align-items-center p-2" style={{ minHeight: '56px' }}>
                                <div className="rounded-circle bg-light d-flex justify-content-center align-items-center" style={{ width: '40px', height: '40px' }}>
                                    <img src={companyInspectionData?.inspector?.profile_image || profile_placeholder} alt='Inspector Profile' className="rounded-circle" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <span className="ms-2 flex-grow-1" style={{ fontWeight: 500 }}>
                                    {companyInspectionData?.inspector?.first_name || companyInspectionData?.inspector?.last_name 
                                        ? `${companyInspectionData?.inspector?.first_name || ''} ${companyInspectionData?.inspector?.last_name || ''}`.trim()
                                        : 'N/A'}
                                </span>
                                {companyInspectionData?.inspector?.phone_no && (
                                    <a
                                        href={`tel:${sanitizePhoneForTel(companyInspectionData?.inspector?.phone_no)}`}
                                        className="d-inline-flex align-items-center justify-content-center"
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            background: '#f3f4f6',
                                            textDecoration: 'none'
                                        }}
                                        aria-label={`Call ${companyInspectionData?.inspector?.first_name || ''} ${companyInspectionData?.inspector?.last_name || ''}`}
                                        title={`Call ${companyInspectionData?.inspector?.first_name || ''} ${companyInspectionData?.inspector?.last_name || ''} (${companyInspectionData?.inspector?.phone_no})`}
                                    >
                                        <i className="ri-phone-line" style={{ fontSize: '1.1rem', color: '#111' }}></i>
                                    </a>
                                )}
                            </div>
                        </div>
                        {companyInspectionData?.status !== 'accepted' && 
                         companyInspectionData?.scheduledVisits?.scheduled_date === undefined && 
                         !companyInspectionData?.PrimaryInspectionReport && (
                                <Button
                                    className="w-100 rounded-0"
                                    color="success"
                                    style={{ fontWeight: 500 }}
                                    onClick={toggleAssignModal}
                                >
                                    Change Inspector
                                </Button>
                            )}
                    </div>
                    {/* Inspection Report Section */}
                    <div className="mb-3">
                        {companyInspectionData?.PrimaryInspectionReport?.reportFileUrl &&
                            <div>
                                <h6 className="mb-3">Inspection Report</h6>
                                <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle bg-light d-flex justify-content-center align-items-center me-3" style={{ width: '48px', height: '48px' }}>
                                        <i className="ri-file-text-line" style={{ fontSize: '2rem', color: '#fa7575ff' }}></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div >Inspection Report</div>
                                        {companyInspectionData?.PrimaryInspectionReport?.file_size && (
                                            <div >{formatFileSize(companyInspectionData?.PrimaryInspectionReport?.file_size)}</div>
                                        )}
                                    </div>
                                    <div
                                        onClick={handleDownloadReport}
                                        style={{ cursor: 'pointer' }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    >
                                        <i className="ri-download-cloud-line" style={{ fontSize: '1.5rem', color: '#222', cursor: 'pointer' }}></i>
                                    </div>
                                </div>
                                {/* Report Created Date */}
                                {companyInspectionData?.PrimaryInspectionReport?.createdAt && (
                                    <div className="d-flex align-items-center justify-content-center mb-3 p-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '8px',
                                            fontSize: '0.875rem'
                                        }}>
                                        <i className="ri-calendar-line me-2" style={{ color: '#6c757d', fontSize: '1rem' }}></i>
                                        <span style={{ color: '#495057', fontWeight: '500' }}>
                                            Report Created: {formatDateTime(companyInspectionData?.PrimaryInspectionReport?.createdAt)}
                                        </span>
                                    </div>
                                )}
                            </div>}
                        {/* Call to Action */}
                        {companyInspectionData?.PrimaryInspectionReport?.rejectionReason && <div
                            className="d-flex flex-column p-3 rounded mb-3"
                            style={{ background: '#f7f8fa' }}
                        >
                            <div className="d-flex align-items-center mb-1">
                                <div
                                    className="rounded-circle d-flex justify-content-center align-items-center me-2"
                                    style={{
                                        width: 28,
                                        height: 28,
                                        background: '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                    }}
                                >
                                    <i className="ri-error-warning-line" style={{ fontSize: 18, color: '#e80e0eff' }}></i>
                                </div>
                                <span className="fw-bold" style={{ fontSize: 18 }}>Call to Action</span>
                            </div>
                            <div style={{ fontSize: 17, lineHeight: 1.3 }}>
                                {companyInspectionData?.PrimaryInspectionReport?.rejectionReason}
                            </div>
                        </div>}
                        {/* {callToAction && <h6 className="mb-3">Updated Report</h6>}

                        <div className="d-flex align-items-center mb-3">
                            <div className="rounded-circle bg-light d-flex justify-content-center align-items-center me-3" style={{ width: '48px', height: '48px' }}>
                                <i className="ri-file-text-line" style={{ fontSize: '2rem', color: '#222' }}></i>
                            </div>
                            <div className="flex-grow-1">
                                <div >Inspection Report</div>
                                <div >123 KBs</div>
                            </div>
                            <div>
                                <i className="ri-download-cloud-line" style={{ fontSize: '1.5rem', color: '#222', cursor: 'pointer' }}></i>
                            </div>
                        </div> */}
                        {companyInspectionData?.PrimaryInspectionReport?.reportFileUrl && companyInspectionData?.PrimaryInspectionReport?.status === 'pending' && <Button className="w-100 rounded-0 mb-2" color="success" style={{ fontWeight: 500 }} onClick={() => { updateReportMutation.mutate({ inspectionReportId: companyInspectionData.PrimaryInspectionReport.id, status: 'approved' }); }} disabled={updateReportMutation.isPending}>
                            {updateReportMutation.isPending ? (<><Spinner size="sm" className='me-2' />Loading...</>) : 'Approve'}
                        </Button>}
                        {companyInspectionData?.PrimaryInspectionReport?.reportFileUrl && companyInspectionData?.PrimaryInspectionReport?.status === 'pending' && <Button className="w-100 rounded-0" color="danger" style={{ fontWeight: 500 }} onClick={() => { setUpdatesModalOpen(true) }}>
                            Call to Action
                        </Button>}
                        {companyInspectionData?.isInspectorPaid === false && companyInspectionData?.PrimaryInspectionReport?.status === 'approved' && <Button className="w-100 rounded-0" color="success" style={{ fontWeight: 500 }} onClick={() => { setTransactionModalOpen(true) }}>
                            Make Payment
                        </Button>}
                    </div>
                </CardBody>
            </Collapse>
            {/* Assign Inspector Modal */}
            <AssignInspectorModal
                isOpen={isAssignModalOpen}
                toggle={toggleAssignModal}
                setInspectionId={setInspectionId || (() => { })}
                inspectionId={inspectionId}
            />
            <TransactionDetailsModal isOpen={transactionModalOpen} toggle={() => setTransactionModalOpen(!transactionModalOpen)} inspectionDetails={companyInspectionData} />
            <UpdatesRequiredModal
                isOpen={updatesModalOpen}
                toggle={handleCallToActionCancel}
                onConfirm={handleCallToActionConfirm}
            />
        </Card>
    );
};

export default CompanyInspection;